import { useCallback, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import { chatRepository } from '../../../services/repositories/ChatRepository';
import { createModuleLogger } from '../../../utils/logger';
import { extractNumber, isValidJid, normalizePhoneForChat } from '../../../utils/phoneUtils';

const log = createModuleLogger('[EnsureConv]');

export const useEnsureConversation = (
    instanceName: string | undefined,
    conversationDbId: string | null,
    activeConversationId: string | null,
    userId: string | null,
    currentOrganizationId: string | undefined,
    setConversationDbId: (id: string) => void,
    directIntegrationId?: string | null,
) => {

    const scope = `${currentOrganizationId}:${directIntegrationId || instanceName}:${activeConversationId}`;
    const scopeRef = useRef(scope);
    scopeRef.current = scope;
    const ensureConversationExists = useCallback(async (text?: string): Promise<string | null> => {
        if (conversationDbId) return conversationDbId;
        if (!userId) return null;

        // VALIDAÇÃO ESTRITA: Impede criação de conversas fantasmas
        // (ex: undefined, null, números muito curtos)
        if (!activeConversationId || !isValidJid(activeConversationId)) {
            log.warn('🚫 Tentativa de criar conversa com ID inválido bloqueada:', activeConversationId);
            return null;
        }

        log.log('⚠️ Conversa não existe no DB, tentando criar...');

        let connectionId = directIntegrationId || null;
        if (!connectionId) {
            let connQuery = supabase
                .from('integrations')
                .select('id')
                .eq('type', 'whatsapp')
                .eq('config->>instance_name', instanceName);
            if (currentOrganizationId) {
                connQuery = connQuery.eq('organization_id', currentOrganizationId);
            }
            const { data: conn } = await connQuery.maybeSingle();
            connectionId = conn?.id || null;
        }

        if (!connectionId) return null;

        const normalizedJid = activeConversationId.startsWith('ig:') ? activeConversationId : normalizePhoneForChat(activeConversationId);
        const newConvId = await chatRepository.upsertConversation(userId, connectionId, {
            id: normalizedJid,
            name: extractNumber(activeConversationId),
            avatar: '',
            unreadCount: 0,
            lastMessage: text || '',
            lastMessageTime: new Date(),
            messages: []
        }, currentOrganizationId);

        if (newConvId && scopeRef.current === scope) {
            setConversationDbId(newConvId);
            log.log('✅ Conversa criada no DB');
        }

        return newConvId;
    }, [conversationDbId, activeConversationId, userId, instanceName, currentOrganizationId, setConversationDbId, directIntegrationId, scope]);

    return { ensureConversationExists };
};
